// N11 / D27: os cards como as IMAGENS do Bubble, copiadas do CDN para o hub
// (`app/public/cards/<...>.png`, servidas como asset estático). O card vira a
// imagem (card-imagem); sem imagem, cai no card estruturado (ícone + título).
//
// Rollout por ÁREA, de propósito. A imagem de um card só vale nas áreas que já têm
// TODAS as imagens, para não misturar card-imagem com card estruturado na MESMA
// página. Por isso o guard é por CURSO (`CURSOS_COM_IMAGEM`), não só "o card tem
// imagem?".
//
// ── FAMÍLIAS DE ARTE ────────────────────────────────────────────────────────
// Um mesmo slug de card tem arte DIFERENTE conforme a área: no Bubble, `aulas` é
// `card-ec-aulas-desk` na Escola, `card-pós-desk-aulas` na Perícia/MBA/pós, e
// `card-aulas` na Aprovação. Então a imagem não é só por slug: é por (família, slug).
//   · raiz (`/cards/<slug>.png`)       → arte da Escola e slugs únicos (sem colisão).
//   · `pos`  (`/cards/pos/<slug>.png`) → arte da família Pós, compartilhada por
//                                        Perícia, MBA e as 7 pós.
//   · `aprovacao` (`/cards/aprovacao/<slug>.png`) → o que a Aprovação tem próprio
//                                        (hoje só `aulas`; o resto cai na raiz).
// A resolução tenta a família primeiro e cai para a raiz — assim a Escola não muda,
// e acrescentar uma pós depois é só mapear o curso para a família `pos`.
//
// As artes das 7 pós saíram do pacote PÚBLICO de cada página do Bubble (o
// `static_js` de `/live/<página>/`, sem login), como as da Escola. Os quatro
// básicos (aulas, comunidade, secretaria, notas) têm um arquivo por página, mas a
// arte é a mesma: reusam os da Perícia. O `hub-dp`, o `calendario-tributario` e o
// `simulador-mei` têm arte de Pós diferente da Escola, por isso estão também na
// família — na Escola continuam com a da raiz.

// Os card_slug com imagem na RAIZ (`app/public/cards/`): arte da Escola + slugs
// únicos de outras áreas que não colidem com a Escola.
const COM_IMAGEM: ReadonlySet<string> = new Set([
  "aulas",
  "comunidade",
  "cfc-de-a-a-z",
  "site-de-questoes",
  "plano-de-estudos",
  "contabiliza-ai",
  "mei-pro",
  "simulador-mei",
  "ir-pro",
  "ir-proposta",
  "gerador-de-curriculos",
  "calendario-tributario",
  "hub-dp",
  "legaliza-facil",
  // Slugs únicos da Aprovação (sem colisão com a Escola) — moram na raiz.
  "gerenciador-de-estudos",
  "tec-concursos",
  "mapa-de-concursos",
]);

// Família de arte por curso. Curso ausente = usa só a raiz (Escola).
const FAMILIA_DO_CURSO: Record<string, string> = {
  pericia: "pos",
  "mba-gestao": "pos",
  "aprovacao-contabil": "aprovacao",
  // escola-contabil / escola-vitalicio → raiz
  "pos-rt": "pos",
  "pos-dp": "pos",
  "pos-cgt": "pos",
  "pos-casp": "pos",
  "pos-financas": "pos",
  "pos-rotinas-simples": "pos",
  "pos-auditoria": "pos",
};

// Por família, os slugs que têm arte PRÓPRIA na pasta da família (override da raiz).
const COM_IMAGEM_POR_FAMILIA: Record<string, ReadonlySet<string>> = {
  pos: new Set([
    "aulas",
    "comunidade",
    "secretaria-do-aluno",
    "minhas-notas",
    "peric-ia",
    "leitor-de-processos",
    "foro-facil",
    // As ferramentas próprias das pós.
    "hub-dp", // pos-dp (arte de Pós; a Escola usa a da raiz)
    "sindifacil", // pos-dp
    "cnaexpert", // pos-dp
    "calculadoras", // pos-rt
    "cst-e-classtrib", // pos-rt
    "central-ibs-cbs", // pos-rt
    "calendario-tributario", // pos-rt e pos-rotinas-simples (arte de Pós)
    "radar-simples", // pos-rotinas-simples
    "simulador-mei", // pos-rotinas-simples (arte de Pós)
    "excel-financas", // pos-financas
    "sheets-financas", // pos-financas
    "ia-financas", // pos-financas
    "sentinela", // pos-auditoria
    "voucher", // pos-auditoria
  ]),
  // A Aprovação só tem `aulas` próprio; `comunidade` e `contabiliza-ai` reusam a raiz.
  aprovacao: new Set(["aulas"]),
};

// Cursos cuja vitrine já está 100% com imagem — onde o card-imagem é ligado.
export const CURSOS_COM_IMAGEM: ReadonlySet<string> = new Set([
  "escola-contabil",
  "escola-vitalicio",
  "pericia",
  "mba-gestao",
  "aprovacao-contabil",
  "pos-rt",
  "pos-dp",
  "pos-cgt",
  "pos-casp",
  "pos-financas",
  "pos-rotinas-simples",
  "pos-auditoria",
]);

/**
 * O caminho da imagem do card, ou `null` (e aí a tela usa o ícone). Só devolve
 * caminho quando o CURSO já está 100% com imagem, para não misturar os dois
 * visuais numa página. Tenta a arte da FAMÍLIA do curso e cai para a raiz.
 */
export function imagemDoCard(cardSlug: string, cursoSlug: string): string | null {
  if (!CURSOS_COM_IMAGEM.has(cursoSlug)) return null;
  const familia = FAMILIA_DO_CURSO[cursoSlug];
  if (familia && COM_IMAGEM_POR_FAMILIA[familia]?.has(cardSlug)) {
    return `/cards/${familia}/${cardSlug}.png`;
  }
  return COM_IMAGEM.has(cardSlug) ? `/cards/${cardSlug}.png` : null;
}
